export class TabsDragReorder {
    static THRESHOLD = 7;
    static SUPPRESS_MS = 250;
    static EDGE = 36;
    static SPEED = 18;

    constructor(
        listEl,
        { onSelect, onReorder, shouldIgnoreTarget, isPinnedItem } = {}
    ) {
        this.el = listEl;
        this.onSelect = typeof onSelect === "function" ? onSelect : () => {};
        this.onReorder = typeof onReorder === "function" ? onReorder : () => {};
        this.ignore =
            typeof shouldIgnoreTarget === "function" ? shouldIgnoreTarget : () => false;
        this.isPinnedItem =
            typeof isPinnedItem === "function" ? isPinnedItem : () => false;
        this.lastOrder = this.#getOrder();
        this.s = null;
        this.suppressUntil = 0;
        this._autoScrollRaf = 0;
        this._autoScrollClientX = 0;
        this.onMove = (e) => {
            const s = this.s;
            if (!s || e.pointerId !== s.pid) return;
            if (s.pinned) return;
            const dx = e.clientX - s.x0;
            const dy = e.clientY - s.y0;
            if (!s.dragging) {
                if (Math.abs(dx) < TabsDragReorder.THRESHOLD && Math.abs(dy) < TabsDragReorder.THRESHOLD)
                    return;
                this.#startDrag(s);
            }
            e.preventDefault();
            this.#moveGhost(s, e.clientX);
            this.#reorder(s, e.clientX);
            this.#autoScroll(e.clientX);
        };

        this.onUp = (e) => {
            const s = this.s;
            if (!s || e.pointerId !== s.pid) return;
            this.#finish(s, e);
        };
    }

    handlePointerDown(e) {
        if (e.button !== 0 || this.ignore(e.target)) return;
        const item = e.target.closest(".result-tabs__item");
        if (!item || !this.el.contains(item)) return;
        this.s = {
            pid: e.pointerId,
            id: item.getAttribute("data-id"),
            item,
            pinned: this.#isPinnedItem(item),
            x0: e.clientX,
            y0: e.clientY,
            dragging: false,
        };
        this.el.setPointerCapture?.(e.pointerId);
        this.el.addEventListener("pointermove", this.onMove, { passive: false });
        this.el.addEventListener("pointerup", this.onUp, { passive: true });
        this.el.addEventListener("pointercancel", this.onUp, { passive: true });
    }

    handleClickFallback(e) {
        if (this.s || Date.now() < this.suppressUntil || this.ignore(e.target)) return false;
        const item = e.target.closest(".result-tabs__item");
        const id = item?.getAttribute?.("data-id");
        if (id == null) return false;
        this.onSelect(id);
        return true;
    }

    #startDrag(s) {
        s.dragging = true;
        const host = this.el.closest(".result-tabs__desktop");
        const r = s.item.getBoundingClientRect();
        const hr = host.getBoundingClientRect();

        const ph = document.createElement("li");
        ph.className = "result-tabs__item result-tabs__item--placeholder";
        ph.style.width = `${r.width}px`;
        ph.style.height = `${r.height}px`;
        s.item.replaceWith(ph);

        s.ph = ph;
        s.startIndex = Array.from(this.el.children).indexOf(ph);
        s.curIndex = s.startIndex;
        s.host = host;
        s.startLeft = host === document.body ? r.left : r.left - hr.left;
        s.width = r.width;

        s.item.classList.add("result-tabs__item--drag-ghost");
        s.item.style.setProperty("position", host === document.body ? "fixed" : "absolute", "important");
        s.item.style.left = `${s.startLeft}px`;
        s.item.style.top = `${host === document.body ? r.top : r.top - hr.top}px`;
        s.item.style.width = `${r.width}px`;
        s.item.style.height = `${r.height}px`;
        s.item.style.transform = "translate3d(0,0,0)";
        s.item.style.zIndex = "9999";
        s.item.style.pointerEvents = "none";
        host.appendChild(s.item);

        host.classList?.add?.("result-tabs__desktop--dragging");
        this.el.classList.add("result-tabs__items--dragging");
    }

    #moveGhost(s, clientX) {
        const hr = s.host?.getBoundingClientRect?.();
        let left = s.startLeft + (clientX - s.x0);
        if (hr) left = Math.min(Math.max(0, left), Math.max(0, hr.width - s.width));
        s.item.style.transform = `translate3d(${left - s.startLeft}px,0,0)`;
    }

    #autoScroll(clientX) {
        this._autoScrollClientX = clientX;
        if (!this._autoScrollRaf) this.#autoScrollTick();
    }

    #autoScrollTick() {
        this._autoScrollRaf = 0;
        const s = this.s;
        if (!s?.dragging || s.pinned) return;

        const el = this.el;
        const r = el.getBoundingClientRect();
        const x = this._autoScrollClientX - r.left;
        const maxScrollLeft = Math.max(0, el.scrollWidth - el.clientWidth);

        let edge = TabsDragReorder.EDGE;
        const root = el.closest?.(".result-tabs") || el.parentElement;
        const raw = root ? getComputedStyle(root).getPropertyValue("--result-tabs-fade-width") : "";
        edge = Math.max(edge, Math.round((parseFloat(raw) || 0) + 24));

        const dir = x < edge ? -1 : x > r.width - edge ? 1 : 0;

        if (dir === 0 || maxScrollLeft <= 0) return;

        const before = el.scrollLeft;
        const next = Math.min(
            maxScrollLeft,
            Math.max(0, before + dir * TabsDragReorder.SPEED)
        );
        if (next === before) return;

        el.scrollLeft = next;
        el.dispatchEvent(new Event("scroll"));
        this.#reorder(s, this._autoScrollClientX);

        this._autoScrollRaf = requestAnimationFrame(() => this.#autoScrollTick());
    }

    #reorder(s, clientX) {
        const r = this.el.getBoundingClientRect();
        const px = this.el.scrollLeft + (clientX - r.left);
        const ph = s.ph;
        if (!ph) return;

        const children = Array.from(this.el.children);
        const pinnedBoundaryIndex = this.#pinnedBoundaryIndex(ph, children);
        let before = null;
        for (const n of children) {
            if (n === ph || !n.classList?.contains?.("result-tabs__item")) continue;
            const mid = (n.offsetLeft || 0) + (n.offsetWidth || 0) / 2;
            if (px < mid) { before = n; break; }
        }

        if (pinnedBoundaryIndex > 0) {
            const proposedIndex = before ? children.indexOf(before) : children.length;
            if (proposedIndex > -1 && proposedIndex < pinnedBoundaryIndex) {
                before = children[pinnedBoundaryIndex] || null;
            }
        }
        if (before === ph) return;

        const prev = new Map();
        this.el.querySelectorAll(".result-tabs__item").forEach((n) => {
            if (n.classList.contains("result-tabs__item--placeholder")) return;
            prev.set(n, n.getBoundingClientRect());
        });

        if (before) {
            if (ph.nextSibling === before) return;
            this.el.insertBefore(ph, before);
        } else {
            if (ph === this.el.lastElementChild) return;
            this.el.appendChild(ph);
        }

        s.curIndex = Array.from(this.el.children).indexOf(ph);
        prev.forEach((from, n) => {
            const to = n.getBoundingClientRect();
            const dx = from.left - to.left;
            if (!dx) return;
            n.style.transition = "none";
            n.style.transform = `translateX(${dx}px)`;
            requestAnimationFrame(() => {
                n.style.transition = "transform 150ms ease";
                n.style.transform = "";
            });
            const done = () => { n.style.transition = ""; n.style.transform = ""; n.removeEventListener("transitionend", done); };
            n.addEventListener("transitionend", done);
        });
    }

    #finish(s, ev) {
        if (this._autoScrollRaf) cancelAnimationFrame(this._autoScrollRaf);
        this._autoScrollRaf = 0;
        this.el.releasePointerCapture?.(s.pid);
        this.el.removeEventListener("pointermove", this.onMove);
        this.el.removeEventListener("pointerup", this.onUp);
        this.el.removeEventListener("pointercancel", this.onUp);

        const didReorder = s.dragging && typeof s.startIndex === "number" && typeof s.curIndex === "number" && s.startIndex !== s.curIndex;
        if (s.ph?.parentNode) s.ph.replaceWith(s.item);
        else if (!s.item.parentNode) this.el.appendChild(s.item);

        s.item.classList.remove("result-tabs__item--drag-ghost");
        s.item.style.removeProperty("position");
        s.item.style.left = "";
        s.item.style.top = "";
        s.item.style.width = "";
        s.item.style.height = "";
        s.item.style.transform = "";
        s.item.style.zIndex = "";
        s.item.style.pointerEvents = "";

        this.el.classList.remove("result-tabs__items--dragging");
        s.host?.classList?.remove?.("result-tabs__desktop--dragging");

        if (didReorder) {
            const order = this.#getOrder();
            this.lastOrder = order;
            try {
                this.onReorder(order);
            } catch (e) {
                console.error(e);
            }
        }

        if (!didReorder) {
            const under =
                typeof document.elementFromPoint === "function"
                    ? document.elementFromPoint(ev.clientX, ev.clientY)
                    : null;
            const id = s.pinned
                ? s.id
                : under?.closest?.(".result-tabs__item")?.getAttribute?.("data-id") ??
                  s.id;
            if (id != null) this.onSelect(id);
        }

        this.suppressUntil = Date.now() + TabsDragReorder.SUPPRESS_MS;
        this.s = null;
    }

    #getOrder() {
        return Array.from(
            this.el.querySelectorAll('.result-tabs__item[data-id]')
        )
            .map((n) => n.getAttribute("data-id"))
            .filter((id) => id != null);
    }

    getOrder() {
        return this.#getOrder();
    }

    #isPinnedItem(itemEl) {
        try {
            if (this.isPinnedItem(itemEl)) return true;
        } catch (_) {}
        const id = itemEl?.getAttribute?.("data-id");
        return String(id) === "0" || itemEl?.classList?.contains?.("result-tabs__item--pinned");
    }

    #pinnedBoundaryIndex(placeholderEl, children) {
        let lastPinnedIndex = -1;
        for (let i = 0; i < children.length; i++) {
            const n = children[i];
            if (n === placeholderEl) continue;
            if (!n?.classList?.contains?.("result-tabs__item")) continue;
            if (this.#isPinnedItem(n)) lastPinnedIndex = i;
        }
        return lastPinnedIndex + 1;
    }
}

